import { useEffect, useRef, useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { isSuccess } from "../../../models/response/base-response";
import { ApiConfig, API_ORDER_REPORT_ROUTER } from "../../../services/api-config";
import EmptyState from "../../../components/custom/empty-state";
import { formatCurrency } from "../../../utils/helpers";
import ServiceTable, { CustomerRow, EMPTY_SUMMARY, SalesOutputReport } from "./service-table";

interface Props {
  customer: CustomerRow;
  startDate: string;
  endDate: string;
  branchId: number | "";
  onClose: () => void;
}

const toDMY = (ymd: string) => {
  const [y, m, d] = (ymd || "").split("-");
  return d && m && y ? `${d}/${m}/${y}` : ymd;
};

/**
 * Dịch vụ khách đã dùng trong kỳ — gọi lại API báo cáo với `customer_id` (cùng bộ lọc ngày/chi nhánh của
 * trang), nên số liệu khớp dòng của khách ở tab "Theo khách hàng".
 */
const CustomerServicesModal: React.FC<Props> = ({ customer, startDate, endDate, branchId, onClose }) => {
  const [report, setReport] = useState<SalesOutputReport | null>(null);
  const [loading, setLoading] = useState(true);
  const { request } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const requestRef = useRef(request);
  useEffect(() => { requestRef.current = request; }, [request]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = {
          from_date: startDate,
          to_date: endDate,
          customer_id: customer.customer_id,
        };
        if (branchId) params.branch_id = branchId;
        const res = await requestRef.current({ endPoint: API_ORDER_REPORT_ROUTER.API_SALES_OUTPUT_REPORT, data: params });
        if (!cancelled && res && isSuccess(res)) setReport(res.data ?? null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customer.customer_id, startDate, endDate, branchId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const services = report?.services ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4" onClick={onClose}>
      <div
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-6xl flex-col rounded-2xl bg-white shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 p-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">
              {customer.customer_code && <span className="mr-2 text-slate-500">{customer.customer_code}</span>}
              {customer.customer_name}
            </h3>
            <p className="mt-0.5 text-sm text-slate-500">
              {customer.customer_phone && <>{customer.customer_phone} · </>}
              Hoàn thành {toDMY(startDate)} → {toDMY(endDate)} · {customer.order_count} đơn · Doanh thu thuần{" "}
              <span className="font-semibold text-blue-700">{formatCurrency(customer.net_amount)}đ</span>
            </p>
          </div>
          <button onClick={onClose} aria-label="Đóng" className="text-lg text-slate-500 hover:text-slate-700">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
            {loading && !report ? (
              <div className="py-16 text-center text-sm text-gray-400">Đang tải...</div>
            ) : services.length === 0 ? (
              <EmptyState message="Chưa có dịch vụ" description="Khách không có dòng dịch vụ nào trong kỳ đã chọn." />
            ) : (
              <ServiceTable rows={services} summary={report?.summary ?? EMPTY_SUMMARY} />
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 p-4 text-right">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-slate-50">Đóng</button>
        </div>
      </div>
    </div>
  );
};

export default CustomerServicesModal;
